import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan, faCarBurst, faEdit, faHandshake, faMailForward, faPause, faRotateRight,
} from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { ReportAccident } from '../../../screens/ClaimsManagmentReportsOptions/ReportAccident';
import { Replacement } from '../../../screens/ClaimsManagmentReportsOptions/Replacement';
import { Cancellation } from '../../../screens/ClaimsManagmentReportsOptions/Cancellation';
import { SendDocument } from '../../../screens/ClaimsManagmentReportsOptions/SendDocument';
import { Modification } from '../../../screens/ClaimsManagmentReportsOptions/Modification';
import { getDocumentTypes } from '../../../features/afterSale/afterSaleActions';
import AfterSaleInfoCards from './AfterSaleInfoCards';
import { afterSaleV2Css } from '../../../screens/CompletedQuotes/CompletedV2.styles';

const AfterSaleStyles = styled.div`
  .section-container {
    margin-bottom: 1.5rem;
  }

  .section-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #00a651;
    
    h2 {
      font-size: 1.25rem;
      font-weight: 700;
      color: #1f2937;
      margin: 0;
    }

    .section-icon {
      color: #00a651;
      font-size: 1.25rem;
    }
  }

  .info-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border: none;
    overflow: hidden;
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
    }
  }

  .card-header-custom {
    background: linear-gradient(135deg, #00a651 0%, #00873f 100%);
    padding: 1rem 1.25rem;
    border: none;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    .header-icon {
      width: 36px;
      height: 36px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      font-size: 1rem;
    }

    .header-title {
      color: #ffffff;
      font-size: 1.1rem;
      font-weight: 600;
      margin: 0;
    }
  }

  .options-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
    padding: 1.25rem;
  }

  .option-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #f0fdf4;
      border-color: #00a651;
      box-shadow: 0 2px 8px rgba(0, 166, 81, 0.15);
      transform: translateY(-2px);
    }

    .option-icon {
      width: 44px;
      height: 44px;
      background: linear-gradient(135deg, #00a651 0%, #00873f 100%);
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      svg {
        color: #ffffff;
        font-size: 1.1rem;
      }
    }

    .option-content {
      flex: 1;
      min-width: 0;

      .option-title {
        font-size: 0.95rem;
        font-weight: 600;
        color: #1f2937;
        margin: 0;
        line-height: 1.3;
      }

      .option-description {
        font-size: 0.8rem;
        color: #6b7280;
        margin-top: 0.25rem;
      }
    }

    .option-arrow {
      color: #9ca3af;
      font-size: 0.875rem;
      transition: transform 0.2s ease;
    }

    &:hover .option-arrow {
      color: #00a651;
      transform: translateX(4px);
    }
  }

  .modal-forms {
    margin-top: 1.5rem;
  }

  @media (max-width: 576px) {
    .options-grid {
      grid-template-columns: 1fr;
      padding: 1rem;
    }

    .option-card {
      padding: 0.875rem 1rem;
    }
  }

  ${(p) => p.$v2 && css`
    && {
      ${afterSaleV2Css}
    }
  `}
`;

function CompletedQuoteScreenAutoAfterSale({ quote, document_types, v2 = false }) {
  const location = useLocation();
  const [selectedOption, setSelectedOption] = useState(null);
  const navigate = useNavigate();

  const postVenditaTypes = {
    1: 'accident',
    2: 'replacement',
    3: 'cancellation',
    4: 'send-documents',
    5: 'modify-data',
    6: 'suspension',
  };

  const optionsList = [
    {
      id: 1,
      icon: faCarBurst,
      title: 'Denuncia Sinistro',
      description: 'Segnala un incidente',
      navigates: true,
    },
    {
      id: 2,
      icon: faRotateRight,
      title: 'Sostituzione',
      description: 'Sostituisci veicolo',
      navigates: true,
    },
    {
      id: 3,
      icon: faBan,
      title: 'Cessazione',
      description: 'Termina polizza',
      navigates: true,
    },
    {
      id: 4,
      icon: faMailForward,
      title: 'Invio documentazione al cliente',
      description: 'Invia documenti',
      navigates: false,
    },
    {
      id: 5,
      icon: faEdit,
      title: 'Modifica dati non tariffari',
      description: 'Aggiorna informazioni',
      navigates: true,
    },
    {
      id: 6,
      icon: faPause,
      title: 'Sospensione',
      description: 'Sospendi polizza',
      navigates: true,
    },
  ];

  const handleOptionClick = (option) => {
    setSelectedOption(option.id);
    if (option.navigates && quote?.id) {
      navigate(`/after-sale/new/${postVenditaTypes[option.id]}/${quote.id}`);
    }
  };

  useEffect(() => {
    if (selectedOption !== null) {
      let type = '';
      switch (selectedOption) {
        case 1:
          type = 'accident';
          break;
        case 2:
          type = 'replacement';
          break;
        case 3:
          type = 'cancellation';
          break;
        case 4:
          type = 'send-documents';
          break;
        case 5:
          type = 'modify-data';
          break;
        case 6:
          type = 'suspension';
          break;
        default:
          break;
      }
      getDocumentTypes(type);
    }
  }, [selectedOption]);

  return (
    <AfterSaleStyles $v2={v2}>
      <AfterSaleInfoCards afterSaleData={quote?.after_sale} v2={v2} />

      <div className="section-container polizze-emessa-status-step-five">
        <div className="section-title">
          <FontAwesomeIcon icon={faHandshake} className="section-icon" />
          <h2>Apri Nuovo Post Vendita</h2>
        </div>

        <div className="info-card">
          <div className="card-header-custom">
            <div className="header-icon">
              <FontAwesomeIcon icon={faHandshake} />
            </div>
            <h3 className="header-title">Operazioni Post Vendita</h3>
          </div>

          {quote !== null && (
            <div className="options-grid">
              {optionsList.map((option) => (
                <div
                  key={option.id}
                  className="option-card"
                  onClick={() => handleOptionClick(option)}
                >
                  <div className="option-icon">
                    <FontAwesomeIcon icon={option.icon} />
                  </div>
                  <div className="option-content">
                    <p className="option-title">{option.title}</p>
                    <span className="option-description">{option.description}</span>
                  </div>
                  <span className="option-arrow">→</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-forms">
          {quote !== null && selectedOption === 1 && (
            <ReportAccident
              quotationId={quote.id}
              selectOption={setSelectedOption}
              location={location}
            />
          )}

          {quote !== null && selectedOption === 2 && (
            <Replacement
              quotationId={quote.id}
              selectOption={setSelectedOption}
              location={location}
              document_types={document_types}
              quote={quote}
            />
          )}

          {quote !== null && selectedOption === 3 && (
            <Cancellation
              quotationId={quote.id}
              selectOption={setSelectedOption}
              location={location}
            />
          )}

          {quote !== null && selectedOption === 4 && (
            <SendDocument
              quotationId={quote.id}
              selectOption={setSelectedOption}
              location={location}
            />
          )}

          {quote !== null && selectedOption === 5 && (
            <Modification
              quotationId={quote.id}
              selectOption={setSelectedOption}
              location={location}
            />
          )}
        </div>
      </div>
    </AfterSaleStyles>
  );
}

export default CompletedQuoteScreenAutoAfterSale;
